import { Component, effect } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { FormValidationService } from '../../../core/services/form-validation.service';
import { ApiError } from '../../../core/models/common/api-error';
import { AuthService } from '../../../core/services/auth.service';
import { Store } from '@ngrx/store';
import { authActions } from '../../../core/store/auth';
import { Router } from '@angular/router';
import { ToastService } from '../../../core/services/toast.service';
import {
  isLoggedIn,
  selectAuthUser,
} from '../../../core/store/reducers';
import { VendorsService } from '../../../core/services/vendors.service';
import { Vendor } from '../../../core/models/entities/vendor';
import { TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-vendor-registration-page',
  template: `
    <app-page-section>
      <div class="grid grid-cols-1 gap-8 lg:grid-cols-3 xl:grid-cols-5">
        <div
          class="shadow-pricing col-span-1 -mx-4 rounded-lg bg-white p-6 md:-mx-0 md:p-8 md:py-16 lg:col-span-2 lg:px-20 xl:col-span-3"
        >
          <h1 class="md:text-center">{{ 'become-host.intro.title' | transloco }}</h1>
          <div class="mt-6" id="signUp">
            {{ 'become-host.intro.description-1' | transloco }}
            <app-text-highlight textSize="sm"
              ><span class="font-bold"
                >{{ 'become-host.intro.highlight' | transloco }}</span
              ></app-text-highlight
            >
            {{ 'become-host.intro.description-2' | transloco }}
          </div>
          <form class="mt-10" (submit)="submit()">
            @if (isLoggedIn() && formWhenLoggedIn) {
              <div class="mb-8">
                <app-alert variant="warning">
                  <i class="far fa-exclamation-triangle mr-2"></i>
                  <span class="font-bold"
                    >{{ 'become-host.form.existing-account-warning-title' | transloco }}</span
                  ><br />
                  {{
                    'become-host.form.existing-account-warning-body'
                      | transloco: { email: authUser()?.email }
                  }}
                </app-alert>
              </div>
              <app-text-input
                [control]="formWhenLoggedIn.get('name')"
                name="name"
                [label]="'become-host.form.company' | transloco"
              ></app-text-input>
            } @else if (form) {
              <div class="grid md:grid-cols-2 md:gap-x-6">
                <app-text-input
                  [control]="form.get('first_name')"
                  name="first_name"
                  [label]="'become-host.form.first-name' | transloco"
                ></app-text-input>
                <app-text-input
                  [control]="form.get('last_name')"
                  name="last_name"
                  [label]="'become-host.form.last-name' | transloco"
                ></app-text-input>
              </div>
              <app-text-input
                [control]="form.get('vendor_name')"
                name="vendor_name"
                [label]="'become-host.form.company' | transloco"
              ></app-text-input>
              <div class="grid md:grid-cols-2 md:gap-x-6">
                <app-text-input
                  [control]="form.get('email')"
                  name="email"
                  [label]="'become-host.form.email' | transloco"
                ></app-text-input>
                <app-password-input
                  [control]="form.get('password')"
                  name="password"
                  [label]="'become-host.form.password' | transloco"
                ></app-password-input>
              </div>
            }

            <div class="mt-6">
              @if (isLoggedIn() && formWhenLoggedIn) {
                <app-single-checkbox-input
                  [control]="formWhenLoggedIn.get('terms_accepted')"
                  name="terms_accepted_logged_in"
                  [label]="'become-host.form.terms' | transloco"
                ></app-single-checkbox-input>
              } @else if (form) {
                <app-single-checkbox-input
                  [control]="form.get('terms_accepted')"
                  name="terms_accepted"
                  [label]="'become-host.form.terms' | transloco"
                ></app-single-checkbox-input>
                <div class="mt-4">
                  <app-single-checkbox-input
                    [control]="form.get('newsletter_subscribed')"
                    name="newsletter_subscribed"
                    [label]="'become-host.form.newsletter' | transloco"
                  ></app-single-checkbox-input>
                </div>
              }
            </div>

            <app-spacer></app-spacer>
            <app-button [loading]="sendingData" type="submit"
              >{{ 'become-host.form.submit' | transloco }}</app-button
            >
          </form>
        </div>
        <div
          class="col-span-1 hidden overflow-visible lg:relative lg:block xl:col-span-2"
        >
          <img
            class="top-1/2 left-[-100px] z-[-1] mx-auto max-w-none -translate-y-1/2 md:absolute md:w-[500px] xl:w-[600px]"
            priority
            ngSrc="devenir-hote-header.png"
            width="1030"
            height="1090"
            sizes="80vw"
            [attr.alt]="'become-host.intro.img-alt' | transloco"
            [attr.title]="'become-host.intro.img-alt' | transloco"
          />
        </div>
      </div>
    </app-page-section>

    <app-page-section>
      <h2 class="mt-10 text-center">{{ 'become-host.steps.title' | transloco }}</h2>

      <div class="mt-20 grid grid-cols-12 items-center">
        <!-- ---------------------------------------------------------------------------->
        <!-- Etape 1 -->
        <!-- ---------------------------------------------------------------------------->

        <div class="col-span-12 md:col-span-8 xl:col-span-6">
          <img
            src="/assets/img/1.svg"
            [attr.alt]="'become-host.steps.step1.img-alt' | transloco"
            [attr.title]="'become-host.steps.step1.img-alt' | transloco"
          />
          <div class="relative -top-12 left-0 lg:left-20 lg:pr-20">
            <h3>{{ 'become-host.steps.step1.title' | transloco }}</h3>
            <p class="mt-6"
              >{{ 'become-host.steps.step1.description' | transloco }}</p
            >
            <div class="mt-6">
              <app-button
                size="sm"
                variant="outlined"
                [routerLink]="['.']"
                fragment="signUp"
                >{{ 'become-host.steps.step1.cta' | transloco }}</app-button
              >
            </div>
          </div>
        </div>
        <div
          class="col-span-12 md:col-span-4 lg:justify-center lg:pr-16 xl:col-span-6"
        >
          <div
            class="relative hidden h-60 w-full translate-y-12 rounded-tr-lg border-t border-r border-gray-400 lg:flex"
          >
            <div class="absolute lg:-top-12 lg:right-1/3">
              <img
                class="w-24"
                ngSrc="coffee.png"
                width="95"
                height="92"
                [attr.alt]="'become-host.steps.decoration-coffee-alt' | transloco"
                [attr.title]="'become-host.steps.decoration-coffee-alt' | transloco"
              />
            </div>
          </div>
        </div>

        <!-- ---------------------------------------------------------------------------->
        <!-- Etape 2 -->
        <!-- ---------------------------------------------------------------------------->

        <div
          class="col-span-12 md:col-span-2 lg:col-span-3 lg:justify-center 2xl:col-span-5"
        >
          <div
            class="relative hidden h-60 w-1/2 translate-y-64 rounded-tl-lg border-t border-l border-gray-400 lg:ml-12 lg:flex xl:ml-24 2xl:w-3/4"
          >
            <div class="absolute top-20 -left-12">
              <img
                class="w-24"
                ngSrc="cles.png"
                width="96"
                height="89"
                [attr.alt]="'become-host.steps.decoration-keys-alt' | transloco"
                [attr.title]="'become-host.steps.decoration-keys-alt' | transloco"
              />
            </div>
          </div>
        </div>

        <div
          class="col-span-12 mt-16 flex justify-end md:col-span-10 lg:col-span-9 2xl:col-span-7"
        >
          <div>
            <div class="relative right-0 flex justify-end">
              <img
                src="/assets/img/2.svg"
                [attr.alt]="'become-host.steps.step2.img-alt' | transloco"
                [attr.title]="'become-host.steps.step2.img-alt' | transloco"
              />
            </div>
            <div class="relative -top-12 md:right-10 md:pl-10">
              <h3
                class="text-right"
                [innerHTML]="'become-host.steps.step2.title' | transloco"
              ></h3>
              <p class="mt-6 xl:text-right"
                >{{ 'become-host.steps.step2.description' | transloco }}</p
              >
              <div class="mt-6 grid grid-cols-1 gap-6 md:grid-cols-3">
                <div
                  class="border-secondary-900 col-span-1 rounded-lg border p-6"
                >
                  <img
                    class="h-16"
                    src="/assets/img/inviter.svg"
                    [attr.alt]="'become-host.steps.step2.img-alt' | transloco"
                    [attr.title]="'become-host.steps.step2.img-alt' | transloco"
                  />
                  <div class="mt-6"
                    >{{ 'become-host.steps.step2.feature-1' | transloco }}</div
                  >
                </div>
                <div
                  class="border-secondary-900 col-span-1 rounded-lg border p-6"
                >
                  <img
                    class="h-16"
                    src="/assets/img/creer-salles.svg"
                    [attr.alt]="'become-host.steps.step2.img-alt' | transloco"
                    [attr.title]="'become-host.steps.step2.img-alt' | transloco"
                  />
                  <div class="mt-6"
                    >{{ 'become-host.steps.step2.feature-2' | transloco }}</div
                  >
                </div>
                <div
                  class="border-secondary-900 col-span-1 rounded-lg border p-6"
                >
                  <img
                    class="h-16"
                    src="/assets/img/reservation-interne.svg"
                    [attr.alt]="'become-host.steps.step2.img-alt' | transloco"
                    [attr.title]="'become-host.steps.step2.img-alt' | transloco"
                  />
                  <div class="mt-6">{{ 'become-host.steps.step2.feature-3' | transloco }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- ---------------------------------------------------------------------------->
        <!-- Etape 3 -->
        <!-- ---------------------------------------------------------------------------->

        <div class="col-span-12 mt-16 md:col-span-10 lg:col-span-8 lg:mt-32">
          <img
            src="/assets/img/3.svg"
            [attr.alt]="'become-host.steps.step3.img-alt' | transloco"
            [attr.title]="'become-host.steps.step3.img-alt' | transloco"
          />
          <div class="relative -top-12 left-0 lg:left-20 lg:pr-20">
            <h3>{{ 'become-host.steps.step3.title' | transloco }}</h3>
            <p class="mt-6 text-pretty"
              >{{ 'become-host.steps.step3.description-1' | transloco }}</p
            >
            <p class="mt-6 text-pretty"
              >{{ 'become-host.steps.step3.description-2' | transloco }}</p
            >
            <div
              class="mt-8 flex flex-col space-y-4 lg:flex-row lg:space-y-0 lg:space-x-8"
            >
              <div>
                <a class="no-underline" href="mailto:contact@ercos.ch">
                  <i class="fal fa-paper-plane mr-1"></i>
                  contact&#64;ercos.ch
                </a>
              </div>
              <div>
                <a class="no-underline" href="tel:0215253060">
                  <i class="fal fa-phone mr-1"></i>
                  021 525 30 60
                </a>
              </div>
              <div>
                <a
                  class="no-underline"
                  href="https://www.ercos.ch"
                  target="_blank"
                >
                  <i class="fal fa-globe mr-1"></i>
                  ercos.ch
                </a>
              </div>
            </div>
          </div>
        </div>

        <div
          class="col-span-12 md:col-span-2 lg:col-span-4 lg:justify-center lg:pr-16"
        >
          <div
            class="relative hidden h-72 w-full translate-y-32 rounded-tr-lg border-t border-r border-gray-400 lg:flex"
          >
            <div class="absolute top-16 -right-16">
              <img
                class="w-32"
                ngSrc="cactus.png"
                width="128"
                height="128"
                [attr.alt]="'become-host.steps.decoration-cactus-alt' | transloco"
                [attr.title]="'become-host.steps.decoration-cactus-alt' | transloco"
              />
            </div>
          </div>
        </div>

        <!-- ---------------------------------------------------------------------------->
        <!-- Etape 4 -->
        <!-- ---------------------------------------------------------------------------->

        <div
          class="col-span-12 hidden md:col-span-4 md:flex lg:col-span-6"
        ></div>

        <div
          class="col-span-12 mt-10 flex justify-end md:col-span-8 lg:col-span-6 lg:mt-16"
        >
          <div>
            <div class="relative right-0 flex justify-end">
              <img
                src="/assets/img/4.svg"
                [attr.alt]="'become-host.steps.step4.img-alt' | transloco"
                [attr.title]="'become-host.steps.step4.img-alt' | transloco"
              />
            </div>
            <div class="relative -top-12 lg:right-10 lg:pl-10">
              <h3
                class="text-right"
                [innerHTML]="'become-host.steps.step4.title' | transloco"
              ></h3>
              <p class="mt-6 xl:text-right"
                >{{ 'become-host.steps.step4.description' | transloco }}</p
              >
            </div>
          </div>
        </div>
      </div>

      <div class="mt-6 flex justify-center">
        <app-button [routerLink]="['./']" fragment="signUp"
          >{{ 'become-host.steps.cta' | transloco }}</app-button
        >
      </div>
    </app-page-section>

    <app-page-section>
      <div class="grid grid-cols-1 items-center md:grid-cols-2">
        <div class="col-span-1">
          <img
            class="mx-auto xl:w-3/4"
            ngSrc="contact-direct.png"
            width="1412"
            height="1352"
            sizes="(max-width: 768px) 80vw, 25vw"
            [attr.alt]="'become-host.contact.img-alt' | transloco"
            [attr.title]="'become-host.contact.img-alt' | transloco"
          />
        </div>
        <div class="col-span-1 mt-12 md:mt-0">
          <div class="flex items-center gap-3 text-secondary-900/40">
            <span class="text-2xl font-light">/</span>
            <i class="fal fa-phone text-secondary-900 text-2xl"></i>
            <span class="text-2xl font-light">/</span>
          </div>
          <h2 class="mt-6">{{ 'become-host.contact.title' | transloco }}</h2>
          <p class="mt-6">{{ 'become-host.contact.description' | transloco }}</p>
          <div class="mt-8 flex flex-wrap gap-3">
            <a href="mailto:contact@ercos.ch">
              <app-button variant="outlined">contact&#64;ercos.ch</app-button>
            </a>
            <a href="tel:0215253060">
              <app-button variant="outlined">021 525 30 60</app-button>
            </a>
            <a href="https://www.ercos.ch" target="_blank">
              <app-button variant="outlined">ercos.ch</app-button>
            </a>
          </div>
        </div>
      </div>
    </app-page-section>
  `,
  standalone: false,
})
export class VendorRegistrationPageComponent {
  form: FormGroup;
  formWhenLoggedIn: FormGroup;
  sendingData = false;
  isLoggedIn = this.store.selectSignal(isLoggedIn);
  authUser = this.store.selectSignal(selectAuthUser);

  constructor(
    private formValidationService: FormValidationService,
    private authService: AuthService,
    private vendorsService: VendorsService,
    private store: Store,
    private router: Router,
    private toastService: ToastService,
    private translocoService: TranslocoService,
  ) {
    effect(() => {
      if (this.isLoggedIn()) {
        this.setupFormWhenLoggedIn();
      } else {
        this.setupFormWhenGuest();
      }
    });
  }

  setupFormWhenGuest() {
    this.form = new FormGroup({
      first_name: new FormControl('', [Validators.required]),
      last_name: new FormControl('', [Validators.required]),
      vendor_name: new FormControl('', [Validators.required]),
      email: new FormControl('', [Validators.required, Validators.email]),
      password: new FormControl('', [Validators.required]),
      terms_accepted: new FormControl(false, [Validators.requiredTrue]),
      newsletter_subscribed: new FormControl(false),
    });
  }

  setupFormWhenLoggedIn() {
    this.formWhenLoggedIn = new FormGroup({
      name: new FormControl('', [Validators.required]),
      terms_accepted: new FormControl(false, [Validators.requiredTrue]),
    });
  }

  async submit() {
    let form: FormGroup;
    if (this.isLoggedIn()) {
      form = this.formWhenLoggedIn;
    } else {
      form = this.form;
    }
    if (this.formValidationService.validate(form)) {
      try {
        this.sendingData = true;
        if (this.isLoggedIn()) {
          const vendorCreated: Vendor = await this.vendorsService.store(
            form.value,
          );
          this.store.dispatch(
            authActions.setVendor({ vendor: vendorCreated }),
          );
          await this.router.navigate(['/salles']);
          this.toastService.success(
            this.translocoService.translate('become-host.form.success-toast'),
          );
          window.location.reload();
        } else {
          const response = await this.authService.registerVendor(form.value);
          this.store.dispatch(authActions.loginSuccess(response));
          await this.redirectAfterLogin();
        }
      } catch (exception) {
        console.error(exception);
        this.formValidationService.displayApiValidationErrors(
          this.form,
          exception as ApiError,
        );
      }
    }
  }

  private async redirectAfterLogin() {
    await this.router.navigate(['/salles']);
    this.toastService.success(
      this.translocoService.translate('become-host.form.success-toast'),
    );
    this.sendingData = false;
  }
}
